import type {
  GameMode,
  GameOverPayload,
  LobbySeat,
  PublicPlayer,
  PlayerStats,
} from "@gaoya/shared";
import { HINTS_PER_MATCH, INITIAL_HP, SCRAMBLE_ITEMS_PER_MATCH, TURN_TIMEOUT_INIT_MS } from "@gaoya/shared";

export type Screen = "boot" | "login" | "hall" | "matching" | "private" | "battle" | "result";

export type PrivateMode = "menu" | "wait" | "join";

export type HallMenu = "home" | "rush-size";

export type ProtoLog = {
  dir: "in" | "out";
  action: string;
  ts: number;
};

export type LeaderRow = {
  nickname: string;
  wins: number;
  title?: string;
};

export type KeyPopup = {
  label: string;
  x: number;
  y: number;
};

export type ScrambleUi = {
  seed: number;
  turnsLeft: number;
};

export type UiState = {
  screen: Screen;
  nickname: string;
  avatarUrl: string;
  avatarImg: CanvasImageSource | null;
  oppAvatarImg: CanvasImageSource | null;
  useWxProfile: boolean;
  wxProfileReady: boolean;
  authDevFallback: boolean;
  userId: string;
  token: string;
  clockOffset: number;
  connected: boolean;
  authed: boolean;
  connecting: boolean;
  connHint: string;

  toast: string;
  toastUntil: number;
  banner: string;
  bannerUntil: number;

  matchingSince: number;
  hallMenu: HallMenu;
  privateMode: PrivateMode;
  roomCode: string;
  joinInput: string;
  shareTicket: string;
  publicMatch: boolean;
  lobbyMaxSeats: number;
  lobbyReadyDeadline: number;

  roomId: string;
  mode: GameMode;
  opponent: PublicPlayer | null;
  peers: PublicPlayer[];
  peerImgs: Record<string, CanvasImageSource | null>;
  peerOnline: Record<string, boolean>;
  lobbySeats: LobbySeat[];
  lobbyHostId: string;
  lobbyCountdownDeadline: number;
  matchDeadline: number;
  scores: Record<string, number>;
  selfHp: number;
  oppHp: number;
  selfPunish: number;
  oppPunish: number;
  round: number;
  currentTurnId: string;
  lastWord: string;
  lastPinyins: string[];
  deadline: number;
  turnMs: number;
  wordChain: string[];
  usedWords: string[];
  opponentOnline: boolean;
  opponentGraceDeadline: number;
  firstPlayerId: string;

  hintsLeft: number;
  hintWord: string | null;
  hintPending: boolean;
  hintPendingUntil: number;
  scrambleItemsLeft: number;
  selfScramble: ScrambleUi | null;
  oppScramble: ScrambleUi | null;
  scramblePending: boolean;
  scramblePendingUntil: number;

  abbr: string;
  candidates: string[];
  lastQueriedAbbr: string;
  queriesThisRound: number;
  lastActionFlash: { word: string; ok: boolean; until: number; playerId?: string } | null;
  scorePop: { playerId: string; until: number } | null;
  selfRacing: boolean;
  shakeUntil: number;
  scrollX: number;
  scrollY: number;
  scrollFollow: boolean;

  gameOver: GameOverPayload | null;

  debug: boolean;
  lastMsgs: ProtoLog[];
  fps: number;

  showLeaderboard: boolean;
  leaderboard: LeaderRow[];
  leaderboardHint: string;
  friendRankCanvas: CanvasImageSource | null;
  /** 大厅好友榜条（微信开放数据域 sharedCanvas，strip 模式） */
  hallRankCanvas: CanvasImageSource | null;
  hallRankKey: string;
  totalWins: number;

  showSettings: boolean;
  privacyAgreed: boolean;
  showPrivacy: boolean;
  bgmMuted: boolean;
  sfxMuted: boolean;
  vibrateMuted: boolean;
  safeTop: number;
  safeBottom: number;
  capsuleLeft: number;
  capsuleTop: number;
  capsuleHeight: number;

  popup: KeyPopup | null;
  pressedKey: string | null;
  pointerDown: boolean;
  pointerX: number;
  pointerY: number;
};

export function randomNickname(): string {
  const n = 1000 + Math.floor(Math.random() * 9000);
  return `茶客${n}`;
}

export function clipNickname(raw: string): string {
  return raw.trim().replace(/\s+/g, " ").slice(0, 16);
}

export function isPlaceholderNick(name: string): boolean {
  return /^茶客\d{4}$/.test(name) || name === "点击授权";
}

export function createState(nickname: string): UiState {
  return {
    screen: "boot",
    nickname,
    avatarUrl: "",
    avatarImg: null,
    oppAvatarImg: null,
    useWxProfile: false,
    wxProfileReady: false,
    authDevFallback: false,
    userId: "",
    token: "",
    clockOffset: 0,
    connected: false,
    authed: false,
    connecting: false,
    connHint: "",

    toast: "",
    toastUntil: 0,
    banner: "",
    bannerUntil: 0,

    matchingSince: 0,
    hallMenu: "home",
    privateMode: "menu",
    roomCode: "",
    joinInput: "",
    shareTicket: "",
    publicMatch: false,
    lobbyMaxSeats: 4,
    lobbyReadyDeadline: 0,

    roomId: "",
    mode: "CHAIN",
    opponent: null,
    peers: [],
    peerImgs: {},
    peerOnline: {},
    lobbySeats: [],
    lobbyHostId: "",
    lobbyCountdownDeadline: 0,
    matchDeadline: 0,
    scores: {},
    selfHp: INITIAL_HP,
    oppHp: INITIAL_HP,
    selfPunish: 0,
    oppPunish: 0,
    round: 0,
    currentTurnId: "",
    lastWord: "",
    lastPinyins: [],
    deadline: 0,
    turnMs: TURN_TIMEOUT_INIT_MS,
    wordChain: [],
    usedWords: [],
    opponentOnline: true,
    opponentGraceDeadline: 0,
    firstPlayerId: "",

    hintsLeft: HINTS_PER_MATCH,
    hintWord: null,
    hintPending: false,
    hintPendingUntil: 0,
    scrambleItemsLeft: SCRAMBLE_ITEMS_PER_MATCH,
    selfScramble: null,
    oppScramble: null,
    scramblePending: false,
    scramblePendingUntil: 0,

    abbr: "",
    candidates: [],
    lastQueriedAbbr: "",
    queriesThisRound: 0,
    lastActionFlash: null,
    scorePop: null,
    selfRacing: true,
    shakeUntil: 0,
    scrollX: 0,
    scrollY: 0,
    scrollFollow: true,

    gameOver: null,

    debug: false,
    lastMsgs: [],
    fps: 0,

    showLeaderboard: false,
    leaderboard: [],
    leaderboardHint: "",
    friendRankCanvas: null,
    hallRankCanvas: null,
    hallRankKey: "",
    totalWins: 0,

    showSettings: false,
    privacyAgreed: false,
    showPrivacy: false,
    bgmMuted: false,
    sfxMuted: false,
    vibrateMuted: false,
    safeTop: 8,
    safeBottom: 0,
    capsuleLeft: 0,
    capsuleTop: 8,
    capsuleHeight: 32,

    popup: null,
    pressedKey: null,
    pointerDown: false,
    pointerX: 0,
    pointerY: 0,
  };
}

export function toast(ui: UiState, text: string, ms = 2200): void {
  ui.toast = text;
  ui.toastUntil = Date.now() + ms;
}

const HIDDEN_CONN = /wss?:\/\/|\b\/ws\b|chengyu\.|storybuddy/i;

/** 给玩家看的连线文案：永不带地址或协议串。 */
export function displayConnHint(ui: UiState): string {
  const fallback = ui.connecting ? "正在推门…" : "茶线重连中";
  const raw = (ui.connHint || "").trim() || fallback;
  if (HIDDEN_CONN.test(raw)) return ui.connecting ? "正在连线…" : "茶线重连中";
  return raw;
}

export function banner(ui: UiState, text: string, ms = 1800): void {
  ui.banner = text;
  ui.bannerUntil = Date.now() + ms;
}

export function pushLog(ui: UiState, dir: "in" | "out", action: string): void {
  ui.lastMsgs.push({ dir, action, ts: Date.now() });
  if (ui.lastMsgs.length > 20) ui.lastMsgs.shift();
}

export function serverNow(ui: UiState, localNow: number): number {
  return localNow + ui.clockOffset;
}

export function remainingMs(ui: UiState, localNow: number): number {
  if (ui.deadline <= 0) return 0;
  return Math.max(0, ui.deadline - serverNow(ui, localNow));
}

export function remainingMatchMs(ui: UiState, localNow: number): number {
  if (ui.matchDeadline <= 0) return 0;
  return Math.max(0, ui.matchDeadline - serverNow(ui, localNow));
}

export function isRush(ui: UiState): boolean {
  return ui.mode === "RUSH";
}

export function isMyTurn(ui: UiState): boolean {
  if (ui.mode === "RUSH") return ui.screen === "battle" && !ui.gameOver && ui.selfRacing;
  return ui.userId.length > 0 && ui.currentTurnId === ui.userId;
}

export function resetBattleInput(ui: UiState): void {
  ui.abbr = "";
  ui.candidates = [];
  ui.lastQueriedAbbr = "";
  ui.queriesThisRound = 0;
  ui.hintWord = null;
  ui.hintPending = false;
  ui.hintPendingUntil = 0;
  ui.popup = null;
  ui.pressedKey = null;
}

export function resetRoom(ui: UiState): void {
  ui.roomId = "";
  ui.roomCode = "";
  ui.shareTicket = "";
  ui.mode = "CHAIN";
  ui.opponent = null;
  ui.peers = [];
  ui.peerImgs = {};
  ui.peerOnline = {};
  ui.lobbySeats = [];
  ui.lobbyHostId = "";
  ui.lobbyCountdownDeadline = 0;
  ui.lobbyReadyDeadline = 0;
  ui.lobbyMaxSeats = 4;
  ui.publicMatch = false;
  ui.hallMenu = "home";
  ui.matchDeadline = 0;
  ui.scores = {};
  ui.round = 0;
  ui.lastWord = "";
  ui.lastPinyins = [];
  ui.wordChain = [];
  ui.usedWords = [];
  ui.currentTurnId = "";
  ui.deadline = 0;
  ui.gameOver = null;
  ui.scrollX = 0;
  ui.scrollY = 0;
  ui.scrollFollow = true;
  ui.opponentOnline = true;
  ui.opponentGraceDeadline = 0;
  ui.hintsLeft = HINTS_PER_MATCH;
  ui.hintWord = null;
  ui.hintPending = false;
  ui.hintPendingUntil = 0;
  ui.scrambleItemsLeft = SCRAMBLE_ITEMS_PER_MATCH;
  ui.selfScramble = null;
  ui.oppScramble = null;
  ui.scramblePending = false;
  ui.scramblePendingUntil = 0;
  ui.scorePop = null;
  ui.selfRacing = true;
  resetBattleInput(ui);
}

export function ownStats(ui: UiState): PlayerStats | null {
  const go = ui.gameOver;
  if (!go) return null;
  return go.stats[ui.userId] ?? null;
}
